<script lang="typescript">
    import { onDestroy } from "svelte";
    import { push } from "svelte-spa-router";
    import { state, setState } from "./stores.js";
    import { MessageType, FocalType, ReadingAdd, Promotion } from "./types";
    import { getRxOptions, getRegion } from "./utils";
    import { onMount } from "svelte";
    import PrescriptionButton from "./PrescriptionButton.svelte";
    import PrescriptionContinueButton from "./PrescriptionContinueButton.svelte";
    import { activePromotions } from "./offers";

    export let params = {};

    let showOver8Message: boolean = false;

    if (!$state.region) {
        setState({
            message: MessageType.SET_REGION,
            region: getRegion(window.location.pathname),
        });
    }

    switch (params.type) {
        case "single":
            setState({
                message: MessageType.DISMISS_SPLASH_SCREEN,
                focalType: FocalType.SINGLE_VISION,
            });
            break;
        case "children-single":
            setState({
                message: MessageType.DISMISS_SPLASH_SCREEN,
                focalType: FocalType.CHILDREN_SINGLE_VISION,
            });
            break;
        case "varifocal":
            setState({
                message: MessageType.DISMISS_SPLASH_SCREEN,
                focalType: FocalType.VARIFOCAL,
            });
            break;
        case "contact-lens":
            setState({
                message: MessageType.CONTACT_LENSES_SELECTED,
            });
            push("/contact-lenses/");
            break;
    }

    if (!$state.splashScreenDismissed) {
        window.location.replace("/");
    }

    const selectRx = (rxId: number) => {
        showOver8Message = false;
        setState({
            message: MessageType.SET_RX,
            rxId,
        });
        if ($state.focalType === FocalType.SINGLE_VISION) {
            push("/lens/");
        }
        if ($state.focalType === FocalType.CHILDREN_SINGLE_VISION) {
            push("/lens/");
        }
    };

    const selectReadingAdd = (readingAdd: ReadingAdd) => {
        setState({
            message: MessageType.SET_READING_ADD,
            readingAdd,
        });
    };

    const stellestClickHandler = (focalType: FocalType) => {
        push("/showcase/stellest/");
    };

    const oxfordStreetClickHandler = (focalType: FocalType) => {
      push("/showcase/oxford-street/");
    };
       


    const transitionDelay = $state.focalType === FocalType.VARIFOCAL ? 400 : 0;

    function* delayGenerator() {
        var idx = 0;
        while (true) {
            yield 200 * idx;
            idx++;
        }
    }

    const delayGen = delayGenerator();

    let varifocalReady: boolean;
    $: varifocalReady =
        $state.focalType === FocalType.VARIFOCAL &&
        $state.rxId !== null &&
        $state.readingAdd !== null;

    let promotions = [];
    $: promotions = activePromotions($state);

    onDestroy(() => window.scrollTo(0, 0));
</script>

<section class="justify-center bg-primary h-screen overflow-y-hidden">
    <div class="page-container h-full bg-primary flex flex-col justify-center items-center">
        <div class="flex flex-row items-center">
            <h1 class="text-5xl mb-8 text-white font-normal">Choose your prescription</h1>
        </div>

        {#if $state.focalType === FocalType.VARIFOCAL}
            <h2 class="text-white text-4xl mt-8 font-normal">Reading Add</h2>
            <div class="flex w-full items-center justify-center space-x-8 pt-10">
                <PrescriptionButton
                    onClick={() => selectReadingAdd(ReadingAdd.LT_2)}
                    isSelected={$state.readingAdd === ReadingAdd.LT_2}
                    transitionParams={{ y: 350, duration: 600, delay: delayGen.next().value }}
                >
                    Up to +2.00
                </PrescriptionButton>
                <PrescriptionButton
                    onClick={() => selectReadingAdd(ReadingAdd.GE_2)}
                    isSelected={$state.readingAdd === ReadingAdd.GE_2}
                    transitionParams={{ y: 350, duration: 600, delay: delayGen.next().value }}
                >
                    +2.25<br />and above
                </PrescriptionButton>
            </div>

            <h2 class="text-white text-4xl mt-12 font-normal">Distance</h2>
        {/if}

        <div class="flex w-full items-center justify-center space-x-8 pt-10">
            {#each getRxOptions($state.focalType) as label, i (label)}
                <PrescriptionButton
                    transitionParams={{ y: 350, duration: 600, delay: delayGen.next().value }}
                    onClick={() => selectRx(i)}
                    isSelected={$state.rxId === i}
                >
                    {@html label}
                </PrescriptionButton>
            {/each}
            {#if $state.focalType !== FocalType.CHILDREN_SINGLE_VISION}
                <PrescriptionButton
                    transitionParams={{
                        y: 350,
                        duration: 600,
                        delay: delayGen.next().value,
                    }}
                    onClick={() => (showOver8Message = true)}
                >
                    +/- 8 and over
                </PrescriptionButton>
            {/if}
        </div>

        {#if showOver8Message}
            <div class="text-lg text-white mt-12">Please refer to our out of range lens offer</div>
        {/if}

        {#if $state.focalType === FocalType.VARIFOCAL}
            <div class="flex w-full items-center justify-center space-x-8 pt-16">
                <PrescriptionContinueButton
                    isActive={varifocalReady}
                    transitionParams={{
                        y: 350,
                        duration: 600,
                        delay: delayGen.next().value,
                    }}
                />
            </div>
        {/if}
        {#if promotions.includes(Promotion.STELLEST_LENS)}
            <div class="text-xl w-full promotion">
                <div class="text-xl text-center text-white mt-16">
                    Have you asked our colleagues about our myopia management Stellest lenses?
                </div>
                <div class="text-xl text-center text-white pt-2">
                    Now available as a sun lens in six different colours
                </div>
                <div
                    class="text-xl text-white text-center italic pt-6"
                    on:click={stellestClickHandler}
                >
                    <div class="inline-block">
                        <img class="w-32 mb-2" src="/img/logos/stellest.png" alt="Stellest logo" />
                        <u>Click here </u>
                    </div>
                    to learn about how they could help your child's vision
                </div>
            </div>
        {/if}
    {#if promotions.includes(Promotion.OXFORD_STREET_BRANDING)}

      <div class="text-xl w-full promotion">
          <div
              class="text-xl text-white text-center mt-16"
              on:click={oxfordStreetClickHandler}
          >
            <div class="inline-block italic">
              <img class="w-16 mb-2 mx-auto" src="/img/logos/essilor.png" alt="Essilor logo" />
              <u>Click here </u> 
            </div>
            to learn about Eyezen Kids lenses - exclusive to Oxford Street
          </div>
      </div>
    {/if}
    </div>
</section>

<style>

.promotion {
  position: relative;
  @apply mt-32;
}

.promotion + .promotion {
  @apply mt-16;
}

.promotion:before {
  --separator-width: 30%;

  content: "";
  position: absolute;
  top: 0;
  left: calc(50% - var(--separator-width) / 2);
  width: var(--separator-width);
  height: 1px;
  border-bottom: 1px solid white;
  opacity: 0.2;
}

</style>
